import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { sessionsApi } from '@/api';
import { SessionInfo, UseSessionSelectorReturn } from '../types';

/**
 * Custom hook for SessionSelector state management and API operations
 */
export const useSessionSelector = (): UseSessionSelectorReturn => {
  const { t } = useTranslation('training');
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingSession, setDeletingSession] = useState<string | null>(null);
  const [editingSession, setEditingSession] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [savingName, setSavingName] = useState<string | null>(null);

  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await sessionsApi.listSessions();

      if (data.success && data.sessions) {
        setSessions((data.sessions || []) as SessionInfo[]);
      } else {
        setError(data.error || t('sessionSelector.errors.failedToLoad'));
      }
    } catch {
      setError(t('sessionSelector.errors.networkError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleEditSessionName = useCallback((sessionId: string, currentName: string) => {
    setEditingSession(sessionId);
    setEditingName(currentName || sessionId.substring(0, 20));
  }, []);

  const handleSaveSessionName = useCallback(async (sessionId: string) => {
    if (!editingName.trim()) {
      alert(t('sessionSelector.errors.emptyName'));
      return;
    }

    setSavingName(sessionId);

    try {
      const data = await sessionsApi.renameSession(sessionId, editingName.trim()) as {
        success: boolean;
        error?: string;
      };

      if (!data.success) {
        throw new Error(data.error || t('sessionSelector.errors.failedToSave'));
      }

      // Update local state
      setSessions(prevSessions =>
        prevSessions.map(session =>
          session.sessionId === sessionId
            ? { ...session, sessionName: editingName.trim() }
            : session
        )
      );

      setEditingSession(null);
      setEditingName('');
    } catch (error) {
      console.error('Error saving session name:', error);
      alert(t('sessionSelector.errors.saveFailed'));
    } finally {
      setSavingName(null);
    }
  }, [editingName, t]);

  const handleCancelEdit = useCallback(() => {
    setEditingSession(null);
    setEditingName('');
  }, []);

  const handleDeleteSession = useCallback(async (
    sessionId: string,
    onSessionDelete?: (sessionId: string) => Promise<void>
  ) => {
    if (!window.confirm(t('sessionSelector.deleteConfirm'))) {
      return;
    }

    setDeletingSession(sessionId);

    try {
      // Uvijek briši sesiju iz baze putem API-ja
      const data = await sessionsApi.deleteSession(sessionId);

      if (!data.success) {
        throw new Error(t('sessionSelector.errors.failedToDelete'));
      }

      // Pozovi callback za dodatnu logiku (npr. čišćenje URL-a)
      if (onSessionDelete) {
        await onSessionDelete(sessionId);
      }

      // Remove session from local state
      setSessions(prevSessions => prevSessions.filter(session => session.sessionId !== sessionId));
    } catch (error) {
      console.error('Error deleting session:', error);
      alert(t('sessionSelector.errors.deleteFailed'));
    } finally {
      setDeletingSession(null);
    }
  }, [t]);

  return {
    sessions,
    loading,
    error,
    deletingSession,
    editingSession,
    editingName,
    savingName,
    loadSessions,
    handleEditSessionName,
    handleSaveSessionName,
    handleCancelEdit,
    handleDeleteSession,
    setEditingName
  };
};
